import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { NzIconModule } from 'ng-zorro-antd/icon';
import type { PassengerAuditViewModel } from '../../pages/audit-detail-page/audit-detail-page.component';

/**
 * Slim, single-row band that sits directly under the seat-first top bar.
 *
 * It shows the passengers (legs) that used the CURRENT seat across the trip and marks the
 * leg under audit. When the seat is empty in booking data it collapses to a thin prompt with
 * "Confirm empty" / "Add rider seen here" actions — the auditor's entry point for an
 * unbooked-rider anomaly.
 *
 * Purely presentational + OnPush: the host binds a STABLE `passengers` array reference
 * (a computed over the per-seat map) so this never re-renders on every change-detection pass.
 */
@Component({
  selector: 'buspro-admin-audit-seat-passenger-stepper',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, NzIconModule],
  templateUrl: './audit-seat-passenger-stepper.component.html',
  styleUrls: ['./audit-seat-passenger-stepper.component.scss'],
})
export class AuditSeatPassengerStepperComponent {
  @Input() passengers: PassengerAuditViewModel[] = [];
  @Input() activePassengerId: string | null = null;
  @Input() isEmpty = false;
  // Recorded unbooked riders on this (booking-empty) seat. >0 means the seat is accounted for,
  // so the band's text reflects "accounted for" vs "empty". The Add-rider / Confirm-empty
  // actions live solely in the right-hand Booking Overview panel (not duplicated here).
  @Input() riderCount = 0;

  @Output() readonly select = new EventEmitter<string>();

  get activeOrdinal(): number {
    const idx = this.passengers.findIndex((p) => p.passenger.id === this.activePassengerId);
    return idx >= 0 ? idx + 1 : 1;
  }

  legTitle(leg: PassengerAuditViewModel): string {
    const name = leg.passenger.customerName || 'Passenger';
    const route =
      leg.originStop && leg.destinationStop ? ` · ${leg.originStop.name} → ${leg.destinationStop.name}` : '';
    return `${name}${route}${leg.passenger.audited ? ' · audited' : ''}`;
  }
}
